import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { useSidebar } from "@/components/ui/sidebar";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import {
  getOutOfMeet,
  selectCurrentRoom,
  setActiveTab,
  clearPatientFocus,
  setShowFileNotif,
  setUnSeenFiles,
} from "@/context/features/room/roomSlice";
import { Phone, Users } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import MicToggle from "../elements/mic-toggle";
import CameraToggle from "../elements/camera-toggle";
import Board from "../elements/board";
import RoomSkeleton from "../skeletons/room-skeleton";
import { useChatSync } from "@/hooks/chat/useChatSync";
import ScreenShareToggle from "../elements/screen-share-toggle";
import {
  useCreateGroupMutation,
  useDeleteRoomMutation,
  useGetGroupByIdLazyQuery,
} from "@/graphql/generated";
import { toaster } from "@/utils/toaster";
import { useTranslation } from "react-i18next";
import { setOpenedGroup } from "@/context/features/chat/chatSlice";
import { TGroup } from "@/types/public";
import {
  selectCurrentAuth,
  updateCredentials,
} from "@/context/features/auth/authSlice";
import { Role } from "@/types/role.enum";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useReceiveFileChange } from "@/hooks/livekit/useReceiveFileChange";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import OpenChat from "../elements/open-chat";
import FileToggle from "../elements/file-toggle";
import { cn } from "@/lib/utils";
import RoleOnly from "@/components/elements/role-only";
import { useIsRole } from "@/hooks/useIsRole";
import { useWakeLock } from "@/hooks/useWakeLock";
import {
  PatientTimerProvider,
  usePatientTimerContext,
} from "@/context/features/room/PatientTimerContext";
import PatientTimerExpiredDialog from "@/components/modules/patients/patient-timer-expired-popover";

function RoomContent() {
  const { livekitContext } = useLivekitContext();
  const {
    toggleCamera,
    toggleMicrophone,
    disconnect,
    startScreenShare,
    stopScreenShare,
    localParticipant,
    room,
    joinLivekitError,
  } = livekitContext ?? {};

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setOpen, setOpenMobile, isMobile, openMobile, toggleSidebar } =
    useSidebar();
  // GraphQL hooks
  const [deleteRoom] = useDeleteRoomMutation();
  const [createGroup] = useCreateGroupMutation();
  const [getGroupById] = useGetGroupByIdLazyQuery();

  const { roomData, patientLine, unSeenFiles, activeTab, isInMeet } =
    useSelector(selectCurrentRoom);
  const { user } = useSelector(selectCurrentAuth);

  const [openAlert, setOpenAlert] = useState<boolean>(false);

  useWakeLock(isInMeet);

  const isHost = useIsRole([Role.HOST]);

  const isAdmin = useMemo(
    () =>
      !!roomData?.adminId && roomData.adminId === localParticipant?.identity,
    [roomData?.adminId, localParticipant?.identity]
  );

  const endCallHandler = useCallback(async () => {
    dispatch(getOutOfMeet());
    if (patientLine) {
      const patientInRoom = patientLine.find(
        (p) => p.role === Role.USER && !p.isDone && p.approved
      );
      if (patientInRoom) {
        setOpenAlert(true);
        return;
      }
    }

    if (!disconnect) return;
    try {
      if (isAdmin) {
        await deleteRoom();
      } else {
        dispatch(getOutOfMeet());
        disconnect();
      }
      dispatch(clearPatientFocus());
    } finally {
      setOpenMobile(false);
      setOpen(false);
    }
  }, [
    disconnect,
    isAdmin,
    deleteRoom,
    dispatch,
    setOpen,
    setOpenMobile,
    patientLine,
  ]);

  const onStartShare = useCallback(async (): Promise<boolean | undefined> => {
    if (!startScreenShare) return;
    await startScreenShare();
    return true;
  }, [startScreenShare]);

  const onStopShare = useCallback(async () => {
    if (!stopScreenShare) return;
    await stopScreenShare();
    return true as const;
  }, [stopScreenShare]);

  useChatSync();

  const didCreateRef = useRef(false);
  const didJoinRef = useRef(false);

  useEffect(() => {
    if (!user || !roomData?.adminId) return;

    const run = async () => {
      try {
        if (user.role.toUpperCase() === Role.USER) {
          if (didCreateRef.current) return;
          if (!(user.id && user.fullName && user.avatarUrl)) return;

          didCreateRef.current = true;
          const res = await createGroup({
            variables: {
              id: user.id.toString(),
              name: user.fullName,
              usersId: roomData.adminId,
              avatarUrl: user.avatarUrl,
            },
          });

          const createdGp = res.data?.chat.createGroup;
          if (createdGp) {
            dispatch(
              setOpenedGroup({
                avatarUrl: createdGp.avatarUrl || "",
                id: createdGp.id || "",
                name: createdGp.name || "",
                dateOfCreate: "",
                usersIds: createdGp.usersIds,
              } as TGroup)
            );
          }
        } else if (user.role.toUpperCase() === Role.GUEST) {
          if (didJoinRef.current) return;
          if (!(user.id && user.visit_guest && user.fullName && user.avatarUrl))
            return;

          didJoinRef.current = true;
          const res = await getGroupById({
            variables: { groupId: user.visit_guest },
          });

          const createdGp = res.data?.chat.getGroupById;
          if (createdGp) {
            dispatch(
              setOpenedGroup({
                avatarUrl: createdGp.avatarUrl || "",
                id: createdGp.id || "",
                name: createdGp.name || "",
                dateOfCreate: "",
                usersIds: createdGp.usersIds,
              } as TGroup)
            );
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    run();
  }, [user, roomData?.adminId, createGroup, getGroupById, dispatch]);

  useReceiveFileChange("file", room, (data) => {
    if (data.type === "add") {
      dispatch(
        setUnSeenFiles(
          activeTab === "files" && openMobile ? 0 : unSeenFiles + 1
        )
      );
      toaster("info", t("file_added"), { position: "top-right" });
    } else if (data.type === "dec") {
      toaster("info", t("file_description_added"), {
        position: "top-right",
      });
    } else {
      toaster("info", t("file_removed"), {
        position: "top-right",
      });
    }

    // if (called) refetch();
    dispatch(setShowFileNotif(true));
  });

  useEffect(() => {
    if (room) return;
    const timeout = setTimeout(() => {
      toaster("error", t("error_occurred"));
      dispatch(getOutOfMeet());
      dispatch(updateCredentials({ approved: false }));

      setOpenMobile(false);
      setOpen(false);
    }, 6000);
    return () => clearTimeout(timeout);
  }, [room, dispatch, setOpen, setOpenMobile, t]);

  // ---------- Error UI ----------
  if (joinLivekitError && joinLivekitError !== "Rome not found") {
    toaster("error", t(joinLivekitError));
  }

  // ---------- Render ----------
  return (
    <Fragment>
      <div className="grow flex flex-col h-full justify-between">
        {/* Room Grid */}
        {room ? <Board /> : <RoomSkeleton />}

        {/* Controls */}
        <div className="py-4 flex items-center justify-center">
          <div
            className={cn(
              "flex justify-center",
              isHost && isMobile ? "gap-1" : "gap-2"
            )}
          >
            <FileToggle />
            <RoleOnly>
              <ScreenShareToggle
                startfunc={onStartShare}
                stopfunc={onStopShare}
              />
            </RoleOnly>
            <MicToggle togglefunc={toggleMicrophone} />
            <CameraToggle togglefunc={toggleCamera} />
            <RoleOnly>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (activeTab === "patients_list") {
                        toggleSidebar();
                      } else {
                        setOpen(true);
                        if (isMobile) setOpenMobile(true);
                      }
                      dispatch(setActiveTab("patients_list"));
                    }}
                  >
                    <Users strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("patients_list")}</TooltipContent>
              </Tooltip>
            </RoleOnly>

            <OpenChat isMain={true} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  size="icon"
                  onClick={endCallHandler}
                >
                  <Phone strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t("exit")}</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      <Dialog open={openAlert} onOpenChange={setOpenAlert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center text-red-400 font-bold text-2xl my-5">
              {t("alert")}
            </DialogTitle>
            <DialogDescription>{t("patient_still_in_room")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setOpenAlert(false)}>{t("close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PatientTimerExpiredDialogWrapper isAdmin={isAdmin} />
    </Fragment>
  );
}

function PatientTimerExpiredDialogWrapper({ isAdmin }: { isAdmin: boolean }) {
  const {
    // expiredPatientIds,
    expiredPatientIdsArray,
  } = usePatientTimerContext();
  return (
    <PatientTimerExpiredDialog
      isAdmin={isAdmin}
      // expiredPatientIds={expiredPatientIds}
      expiredPatientIdsArray={expiredPatientIdsArray}
    />
  );
}

function Room() {
  return (
    <PatientTimerProvider>
      <RoomContent />
    </PatientTimerProvider>
  );
}

export default memo(Room);
